/**
 * Mecenas LES-LEX — Professional AI Help Chatbot.
 *
 * Features:
 *   - SSE streaming responses (real-time text appearance)
 *   - Context-aware suggested questions (change per page)
 *   - RAG sources displayed with relevance scores
 *   - Persistent conversation (localStorage, last 20 messages)
 *   - First-visit pulse animation + notification dot
 *   - Formal Polish ("Pan/Pani")
 *   - Fallback to non-streaming on SSE failure
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  GraduationCap, X, Send, Sparkles, BookOpen, ChevronDown,
} from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "mecenas_conversations";
const OPENED_KEY = "mecenas_opened_once";
const MAX_MESSAGES = 20;

const GREETING = `Dzień dobry! Jestem **Mecenas LES-LEX** — Państwa asystent platformy.

Mogę pomóc w:
- Tworzeniu pism i konfiguracji systemu
- Wyjaśnieniu funkcji (Kreator, Studio, Detektyw AI...)
- Ustawieniu stylu kancelarii
- Rozwiązaniu problemów technicznych

W czym mogę pomóc?`;

function getSuggestedQuestions(pathname) {
  if (pathname.includes("/creator")) {
    return [
      "Jak wygenerować pozew?",
      "Jak dodać materiały do pisma?",
      "Ile jest typów dokumentów?",
    ];
  }
  if (pathname.includes("/studio")) {
    return [
      "Jak działa Studio AI?",
      "Skąd AI bierze orzeczenia?",
      "Jak poprosić o recenzję pisma?",
    ];
  }
  if (pathname.includes("/sources")) {
    return [
      "Jakie formaty plików są obsługiwane?",
      "Jak działa OCR na skanach?",
      "Ile plików mogę dodać?",
    ];
  }
  if (pathname.includes("/detective")) {
    return [
      "Jak sprawdzić kontrahenta?",
      "Co sprawdza tryb Dogłębny?",
      "Czy są listy sankcyjne?",
    ];
  }
  if (pathname.includes("/advice")) {
    return [
      "Czym różni się szybka od pogłębionej porady?",
      "Jak działa optymalizacja podatkowa?",
      "Czy AI daje porady prawne?",
    ];
  }
  if (pathname.includes("/settings")) {
    return [
      "Jak ustawić styl kancelarii?",
      "Jak włączyć weryfikację dwuetapową?",
      "Jak zmienić plan?",
    ];
  }
  if (pathname.includes("/pricing")) {
    return [
      "Czym różnią się plany?",
      "Ile kosztuje plan Kancelaria?",
      "Czy mogę testować za darmo?",
    ];
  }
  return [
    "Jak zacząć pracę z LES-LEX?",
    "Jak ustawić styl kancelarii?",
    "Jak wygenerować pismo?",
  ];
}

function loadMessages() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved).slice(-MAX_MESSAGES);
  } catch {}
  return [{ role: "assistant", content: GREETING, sources: [] }];
}

function saveMessages(messages) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-MAX_MESSAGES)));
  } catch {}
}

export default function MecenasChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadMessages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPulse, setShowPulse] = useState(!localStorage.getItem(OPENED_KEY));
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  const handleOpen = () => {
    setOpen(true);
    setShowPulse(false);
    localStorage.setItem(OPENED_KEY, "1");
    setTimeout(() => inputRef.current?.focus(), 200);
  };

  const handleClear = () => {
    setMessages([{ role: "assistant", content: GREETING, sources: [] }]);
  };

  const handleSend = useCallback(async (text) => {
    const question = (text || input).trim();
    if (!question || loading) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: question }]);
    setLoading(true);

    // Try SSE streaming first
    try {
      const token = localStorage.getItem("les_lex_token");
      const response = await fetch(`/api/help/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          question,
          current_page: location.pathname,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error("SSE not available");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      let sources = [];

      // Add empty assistant message
      setMessages(prev => [...prev, { role: "assistant", content: "", sources: [] }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === "sources") {
              sources = event.sources || [];
            } else if (event.type === "delta") {
              assistantText += event.text;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "assistant",
                  content: assistantText,
                  sources,
                };
                return updated;
              });
            } else if (event.type === "done") {
              // Final update with sources
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: "assistant",
                  content: assistantText,
                  sources,
                };
                return updated;
              });
            } else if (event.type === "error") {
              throw new Error(event.message);
            }
          } catch (e) {
            if (e.message !== "SSE not available") continue;
            throw e;
          }
        }
      }
    } catch {
      // Fallback to non-streaming
      try {
        const res = await api.post("/api/help/ask-sync", {
          question,
          current_page: location.pathname,
        }, { timeout: 30000 });

        setMessages(prev => {
          // Remove empty streaming message if exists
          const cleaned = prev.filter((m, i) => !(i === prev.length - 1 && m.role === "assistant" && !m.content));
          return [...cleaned, {
            role: "assistant",
            content: res.data?.answer || "Przepraszam, nie udało się uzyskać odpowiedzi.",
            sources: res.data?.sources || [],
          }];
        });
      } catch {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "Przepraszam, nie udało się połączyć z asystentem.\n\nNajczęściej pytane:\n- Jak zacząć? → kliknij \"Nowa sprawa\" na Pulpicie\n- Jak wygenerować pismo? → otwórz projekt → Kreator\n- Pomoc: support@leslex.pl",
          sources: [],
        }]);
      }
    } finally {
      setLoading(false);
    }
  }, [input, loading, location.pathname]);

  const suggested = getSuggestedQuestions(location.pathname);

  return (
    <>
      {/* Floating button — bottom LEFT (right has bug report) */}
      <button
        onClick={open ? () => setOpen(false) : handleOpen}
        className={`fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full shadow-lg
                    flex items-center justify-center transition-all duration-300
                    ${open
                      ? "bg-gray-600 text-white scale-90"
                      : "bg-gradient-to-br from-primary to-primary/80 text-white hover:scale-110"
                    }
                    ${showPulse && !open ? "animate-pulse ring-4 ring-primary/30" : ""}
                    `}
        title="Mecenas LES-LEX — pomoc"
      >
        {open ? <X className="h-5 w-5" /> : <GraduationCap className="h-7 w-7" />}
        {showPulse && !open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full border-2 border-white" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 left-6 z-50 w-[400px] max-w-[90vw]
                     bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border
                     flex flex-col overflow-hidden
                     animate-in fade-in slide-in-from-bottom-4 duration-200"
          style={{ height: "520px", maxHeight: "72vh" }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-6 w-6" />
              <div>
                <h3 className="font-bold text-sm">Mecenas LES-LEX</h3>
                <p className="text-[10px] text-white/70">Asystent pomocy platformy</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleClear} className="p-1.5 rounded-full hover:bg-white/20 transition-colors" title="Nowa rozmowa">
                <Sparkles className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/20 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, i) => (
              <div key={i}>
                <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[88%] rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                      msg.role === "user"
                        ? "bg-primary text-white rounded-br-sm"
                        : "bg-gray-100 dark:bg-gray-800 text-foreground rounded-bl-sm"
                    }`}
                  >
                    {msg.content || (loading && i === messages.length - 1 ? (
                      <span className="animate-pulse text-muted-foreground">Szukam odpowiedzi...</span>
                    ) : "")}
                  </div>
                </div>
                {/* Sources */}
                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-1 ml-1 flex flex-wrap gap-1">
                    {msg.sources.map((s, j) => (
                      <span
                        key={j}
                        className="inline-flex items-center gap-1 text-[9px] bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full"
                        title={`${s.title} — ${s.section} (${Math.round(s.score * 100)}%)`}
                      >
                        <BookOpen className="h-2.5 w-2.5" />
                        {s.title?.slice(0, 30)}
                        <span className="text-blue-400">{Math.round(s.score * 100)}%</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Suggested questions */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {suggested.map((q) => (
                <button
                  key={q}
                  onClick={() => handleSend(q)}
                  className="text-[11px] bg-primary/5 border border-primary/20 text-primary px-2.5 py-1 rounded-full hover:bg-primary/10 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t p-3 flex gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Zadaj pytanie o system..."
              className="flex-1 text-sm resize-none rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background"
              rows={2}
              maxLength={1000}
              disabled={loading}
            />
            <Button size="icon" onClick={() => handleSend()} disabled={loading || !input.trim()} className="shrink-0 self-end">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
